import React, { useState, useEffect } from "react";
import {
  Copy,
  CheckCircle,
  Share2,
  X,
  Wallet,
  History,
  CreditCard,
  Clock,
  AlertCircle,
  Check,
  XCircle,
  Plus,
  Minus,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  User,
  Mail,
  Phone,
  TrendingUp,
  TrendingDown,
  FileText,
  Calendar,
  Eye,
  Loader2,
  Download,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";
import UserDocumentViewer from "./UserDocumentViewer";
import { useSelector, useDispatch } from "react-redux";
import {
  checkUserLogin,
  fetchUserDetailsbyRef,
} from "../redux/reduxThunk/thunkAuthUser/AuthUser";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import UserVisaApplications from "./UserVisaApplications.jsx";

const UserProfile = () => {
  const { user } = useAuth();
  const { currency } = useCurrency();
  const dispatch = useDispatch();

  const { authdata } = useSelector((state) => state?.auth);

  const [refferanceCount, setRefferanceCount] = useState(0);
  const [openModel, setOpenModel] = useState(false);
  const [refferanceData, setRefferanceData] = useState([]);
  const [copied, setCopied] = useState(false);
  const [userWallet, setUserWallet] = useState(0);
  const [walletStatus, setWalletStatus] = useState("not_active"); // not_active, pending, active, deactivated
  const [walletHistory, setWalletHistory] = useState([]);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showFundRequestModal, setShowFundRequestModal] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [fundNote, setFundNote] = useState("");
  const [userRequests, setUserRequests] = useState([]);
  const [activeWalletTab, setActiveWalletTab] = useState("balance");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Increased for better view
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalAdded: 0,
    totalDeducted: 0,
    transactionCount: 0,
  });
  const [activityBookings, setActivityBookings] = useState([]);
  const [activityBookingsLoading, setActivityBookingsLoading] = useState(false);
  const [selectedActivityBooking, setSelectedActivityBooking] = useState(null);
  const [eTicketDownloadingId, setETicketDownloadingId] = useState(null);

  const url = import.meta.env.VITE_URL;
  const getUserId = () => {
    // Multiple sources se try karo
    if (user?._id) return user._id;
    if (user?.id) return user.id;
    if (authdata?.token?._id) return authdata.token._id;

    // LocalStorage se try karo
    const localUser = localStorage.getItem("user");
    if (localUser) {
      try {
        const parsed = JSON.parse(localUser);
        return parsed._id || parsed.id;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (getUserId()) {
      fetchUserWalletAndRequests();
    }
  }, [url, user?._id, authdata?.token?._id]);

  // ✅ FETCH USER WALLET STATUS & REQUESTS (uses my-balance, my-wallet-history, my-requests – no userId in URL)
  const fetchUserWalletAndRequests = async () => {
    try {
      setLoading(true);

      // 1. Check wallet status and balance
      const walletRes = await axios.get(`${url}/api/wallet/my-balance`, {
        withCredentials: true,
      });

      if (walletRes.data.success && walletRes.data.data.length > 0) {
        setUserWallet(walletRes.data.data[0].total);
        setWalletStatus("active");

        // Fetch wallet history (user's own – no userId in URL)
        try {
          const historyRes = await axios.get(
            `${url}/api/wallet/my-wallet-history`,
            { withCredentials: true }
          );
          if (historyRes.data.success) {
            const history = historyRes.data.data || [];
            setWalletHistory(history);
            const totalAdd = history
              .filter((t) => t.type === "ADD")
              .reduce((sum, t) => sum + t.amount, 0);
            const totalDeduct = history
              .filter((t) => t.type === "DEDUCT")
              .reduce((sum, t) => sum + t.amount, 0);
            setStats({
              totalAdded: totalAdd,
              totalDeducted: totalDeduct,
              transactionCount: history.length,
            });
          }
        } catch (historyErr) {
          // History not available (e.g. endpoint not deployed yet)
        }
      } else {
        setUserWallet(0);
        try {
          const statusRes = await axios.get(
            `${url}/api/wallet/my-wallet-status`,
            { withCredentials: true }
          );
          if (statusRes.data.success) {
            const { isActive, latestRequest } = statusRes.data.data;
            if (isActive) setWalletStatus("active");
            else if (latestRequest?.status === "pending") setWalletStatus("pending");
            else if (latestRequest?.status === "deactivated") setWalletStatus("deactivated");
            else setWalletStatus("not_active");
          } else {
            setWalletStatus("not_active");
          }
        } catch (_) {
          setWalletStatus("not_active");
        }
      }

      // 2. Fetch user's own requests (no userId in URL)
      try {
        const userRequestsRes = await axios.get(
          `${url}/api/wallet/my-requests`,
          { withCredentials: true }
        );
        if (userRequestsRes.data.success) {
          setUserRequests(userRequestsRes.data.data || []);
        }
      } catch (_) {
        // Requests endpoint not available
      }
    } catch (err) {
      console.error("Wallet fetch error:", err);
      setWalletStatus("not_active");
    } finally {
      setLoading(false);
    }
  };

  // ✅ ACTIVATE WALLET REQUEST
  const handleWalletActivate = async () => {
    try {
      // Check if already has pending request
      const pendingRequest = userRequests.find(
        (req) => req.status === "pending" && req.requestType === "activation"
      );

      if (pendingRequest) {
        toast.info("You already have a pending activation request");
        return;
      }

      await axios.post(
        `${url}/api/wallet/request-activation`,
        {
          note: "Wallet activation request",
        },
        { withCredentials: true }
      );

      toast.success("Wallet activation request submitted!");
      setWalletStatus("pending");
      fetchUserWalletAndRequests();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Request failed");
    }
  };

  // ✅ REACTIVATE WALLET (After deactivation)
  const handleWalletReactivate = async () => {
    try {
      await axios.post(
        `${url}/api/wallet/request-activation`,
        {
          note: "Wallet reactivation request",
        },
        { withCredentials: true }
      );

      toast.success("Wallet reactivation request submitted!");
      setWalletStatus("pending");
      fetchUserWalletAndRequests();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Request failed");
    }
  };

  // ✅ REQUEST FUND ADDITION
  const handleFundRequest = async () => {
    if (!fundAmount || fundAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    // Check if already has pending fund request
    const pendingFundRequest = userRequests.find(
      (req) => req.status === "pending" && req.requestType === "fund_add"
    );

    if (pendingFundRequest) {
      toast.info(
        `You already have a pending fund request of ${formatPriceSync(Number(pendingFundRequest.requestedAmount) || 0, currency)}`
      );
      return;
    }

    try {
      await axios.post(
        `${url}/api/wallet/request-fund`,
        {
          requestedAmount: parseFloat(fundAmount),
          note: fundNote || "Fund addition request",
        },
        { withCredentials: true }
      );

      toast.success(`Fund request of ${formatPriceSync(Number(fundAmount) || 0, currency)} submitted!`);
      setFundAmount("");
      setFundNote("");
      setShowFundRequestModal(false);
      fetchUserWalletAndRequests();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Request failed");
    }
  };

  useEffect(() => {
    if (user?._id) {
      fetchUserWalletAndRequests();
    }
  }, [url, user?._id]);

  useEffect(() => {
    if (!user?._id && !authdata?.token?._id) return;
    setActivityBookingsLoading(true);
    axios
      .get(`${url}/api/activity/user/bookings`, { withCredentials: true })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setActivityBookings(res.data.data);
        }
      })
      .catch(() => setActivityBookings([]))
      .finally(() => setActivityBookingsLoading(false));
  }, [url, user?._id, authdata?.token?._id]);

  const handleDownloadETicket = async (bookingId, bookingRef) => {
    const base = url || import.meta.env.VITE_URL2 || "http://localhost:3000";
    setETicketDownloadingId(bookingId);
    try {
      const res = await axios.get(`${base}/api/activity/user/bookings/${bookingId}/e-ticket`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `E-Ticket-${bookingRef || bookingId}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          toast.error(j.error || "Failed to download e-ticket.");
        } catch {
          toast.error("Failed to download e-ticket.");
        }
      } else {
        toast.error(data?.error || "Failed to download e-ticket.");
      }
    } finally {
      setETicketDownloadingId(null);
    }
  };

  useEffect(() => {
    dispatch(checkUserLogin());
  }, [dispatch, user]);

  useEffect(() => {
    if (!authdata?.token?.generatedRefId) return;

    const refID = authdata.token.generatedRefId;

    dispatch(fetchUserDetailsbyRef(refID)).then((res) => {
      setRefferanceData(res?.payload?.data || []);
      if (res?.payload?.success) {
        setRefferanceCount(res.payload.data.length);
      }
    });
  }, [dispatch, authdata, user]);

  const userData = authdata?.token;
  const referralCode = userData?.generatedRefId;
  const referralLink = `${window.location.origin}/login?ref=${referralCode}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Link copied to clipboard!");
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join Sky Club",
          text: "Register using my referral link and get rewards!",
          url: referralLink,
        });
      } catch (error) {
        console.error("Share cancelled", error);
      }
    } else {
      handleCopy();
    }
  };

  // ✅ PAGINATION FOR HISTORY
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentHistoryItems = walletHistory.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(walletHistory.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 5;

    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      pageNumbers.push(1);

      if (startPage > 2) {
        pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  // ✅ FORMAT DATE
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {/* MAIN CARD */}
      <div className="max-w-4xl mx-auto mt-8 mb-12 px-4 sm:px-6">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-6 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Profile
            </h1>
            <p className="text-slate-200 text-sm mt-1">
              Manage your account and wallet
            </p>
          </div>

          <div className="p-6 sm:p-8">
            {/* User Information */}
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Account details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</span>
                  <span className="font-medium text-gray-900 mt-0.5">{userData?.username || "—"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email</span>
                  <span className="font-medium text-gray-900 mt-0.5">{userData?.email || "—"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</span>
                  <span className="font-medium text-gray-900 mt-0.5">{userData?.phone || "N/A"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">City</span>
                  <span className="font-medium text-gray-900 mt-0.5">{userData?.hometowncity || "—"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</span>
                  <span className="font-medium text-gray-900 mt-0.5">{userData?.gender || "—"}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                  <div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">Referrals</span>
                    <span className="font-medium text-gray-900 mt-0.5">{refferanceCount}</span>
                  </div>
                  <button
                    onClick={() => setOpenModel(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                </div>
              </div>
            </section>

            {/* Wallet section */}
            <section className="mt-8">
              <div className="p-5 sm:p-6 border border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-slate-50">
                <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-emerald-600" />
                    Wallet
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={fetchUserWalletAndRequests}
                      disabled={loading}
                      className="p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50 transition-colors"
                      title="Refresh"
                    >
                      <RefreshCw
                        className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                      />
                    </button>
                    <button
                      onClick={() => setShowWalletModal(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </button>
                  </div>
                </div>

            {/* 🔴 CASE 1: WALLET NOT ACTIVE */}
            {walletStatus === "not_active" && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800">
                      Wallet Not Activated
                    </p>
                    <p className="text-sm text-yellow-700">
                      Activate wallet to manage funds and make payments
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleWalletActivate}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 rounded-lg text-white hover:from-blue-700 hover:to-blue-800 transition-all flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  Request Wallet Activation
                </button>
              </div>
            )}

            {/* 🟡 CASE 2: PENDING ACTIVATION/REACTIVATION REQUEST */}
            {walletStatus === "pending" && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-yellow-600 animate-pulse" />
                  <div>
                    <p className="font-medium text-yellow-800">
                      ⏳ Request Pending
                    </p>
                    <p className="text-sm text-yellow-700">
                      Our <strong>Celestial team</strong> will review your
                      request soon.
                    </p>
                  </div>
                </div>

                {/* Show pending requests */}
                {userRequests.filter((r) => r.status === "pending").length >
                  0 && (
                  <div className="mt-3 text-sm">
                    <p className="font-medium">Pending Requests:</p>
                    {userRequests
                      .filter((r) => r.status === "pending")
                      .map((req) => (
                        <div
                          key={req._id}
                          className="flex items-center justify-between mt-1 p-2 bg-white border rounded"
                        >
                          <span>
                            {req.requestType === "activation"
                              ? "Wallet Activation"
                              : req.requestType === "reactivation"
                              ? "Wallet Reactivation"
                              : `Fund Add (${formatPriceSync(Number(req.requestedAmount) || 0, currency)})`}
                          </span>
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                            Pending
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}

            {/* 🔵 CASE 3: DEACTIVATED WALLET */}
            {walletStatus === "deactivated" && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <XCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-medium text-red-800">
                      Wallet Deactivated
                    </p>
                    <p className="text-sm text-red-700">
                      Your wallet has been deactivated. You can request
                      reactivation.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleWalletReactivate}
                  className="bg-gradient-to-r from-orange-600 to-orange-700 px-4 py-2 rounded-lg text-white hover:from-orange-700 hover:to-orange-800 transition-all flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Request Reactivation
                </button>
              </div>
            )}

            {/* 🟢 CASE 4: ACTIVE WALLET */}
            {walletStatus === "active" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-emerald-800">
                          Current Balance
                        </p>
                        <p className="text-3xl font-bold text-emerald-900 mt-1">
                          {formatPriceSync(Number(userWallet) || 0, currency)}
                        </p>
                      </div>
                      <div className="p-2 bg-emerald-500/20 rounded-full">
                        <Wallet className="w-6 h-6 text-emerald-700" />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-800">
                          Actions
                        </p>
                        <p className="text-sm text-blue-700 mt-1">
                          Add funds or view history
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowFundRequestModal(true)}
                          className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add Funds
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                {walletHistory.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <p>
                      Last transaction:{" "}
                      {walletHistory[0]?.type === "ADD" ? "Added" : "Deducted"}{" "}
                      {formatPriceSync(Number(walletHistory[0]?.amount) || 0, currency)}
                      <span className="text-gray-400 ml-2">
                        {new Date(
                          walletHistory[0]?.createdAt
                        ).toLocaleDateString()}
                      </span>
                    </p>
                  </div>
                )}
              </>
            )}
              </div>
            </section>

            {/* Activity Bookings */}
            <section className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                Activity Bookings
              </h2>
              {activityBookingsLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : activityBookings.length === 0 ? (
                <div className="p-6 border border-gray-200 rounded-xl bg-gray-50 text-center text-gray-600">
                  No activity bookings yet. Book an activity to see them here.
                </div>
              ) : (
                <div className="space-y-3">
                  {activityBookings.map((b) => {
                    const activity = b.activityOptionId?.activityId;
                    const city = activity?.cityId;
                    const country = city?.countryId;
                    const option = b.activityOptionId;
                    const date = b.selectedDateTime ? new Date(b.selectedDateTime) : null;
                    return (
                      <div
                        key={b._id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedActivityBooking(b)}
                        onKeyDown={(e) => e.key === "Enter" && setSelectedActivityBooking(b)}
                        className="p-4 border border-gray-200 rounded-xl bg-white hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <div className="flex flex-wrap justify-between items-start gap-2">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-slate-500 shrink-0" />
                            <div>
                              <div className="font-semibold text-gray-900">
                                {activity?.name || "Activity"} • {option?.name || "Option"}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">
                                {country?.name || ""} {city?.name ? `→ ${city.name}` : ""}
                              </div>
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5" />
                                {date ? date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900">
                              {formatPriceSync(Number(b.totalPrice) || 0, currency)}
                            </div>
                            <div className="text-xs text-gray-500">{b.bookingReference || "—"}</div>
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                              b.paymentMethod === "wallet" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"
                            }`}>
                              {b.paymentMethod === "wallet" ? "Wallet" : "Card"}
                            </span>
                            <span className={`inline-block mt-1 ml-1 px-2 py-0.5 rounded text-xs font-medium ${
                              b.status === "confirmed" || b.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : b.status === "cancelled" || b.status === "rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-amber-100 text-amber-800"
                            }`}>
                              {b.status || "pending"}
                            </span>
                            {b.eTicketPdfPath && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleDownloadETicket(b._id, b.bookingReference); }}
                                disabled={eTicketDownloadingId === b._id}
                                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200 disabled:opacity-50"
                              >
                                {eTicketDownloadingId === b._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                {eTicketDownloadingId === b._id ? "Downloading…" : "Download e-ticket"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Activity Booking Detail Modal */}
            {selectedActivityBooking && (
              <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                onClick={() => setSelectedActivityBooking(null)}
              >
                <div
                  className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900">Booking Details</h3>
                    <button
                      type="button"
                      onClick={() => setSelectedActivityBooking(null)}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="p-6 space-y-5">
                    {(() => {
                      const b = selectedActivityBooking;
                      const activity = b.activityOptionId?.activityId;
                      const city = activity?.cityId;
                      const country = city?.countryId;
                      const option = b.activityOptionId;
                      const date = b.selectedDateTime ? new Date(b.selectedDateTime) : null;
                      const pb = b.priceBreakdown;
                      const visitorDetailsList = Array.isArray(b.visitorDetails) ? b.visitorDetails : [];
                      return (
                        <>
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Activity & Option</div>
                            <div className="font-semibold text-gray-900">{activity?.name || "—"} • {option?.name || "—"}</div>
                            <div className="text-sm text-gray-600 mt-0.5">{country?.name || ""} {city?.name ? ` → ${city.name}` : ""}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</div>
                              <div className="font-medium">{date ? date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Booking Reference</div>
                              <div className="font-mono text-sm font-medium">{b.bookingReference || "—"}</div>
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Visitors & Details</div>
                            <div className="space-y-3">
                              {visitorDetailsList.length === 0 ? (
                                <p className="text-sm text-gray-500">No visitor details stored.</p>
                              ) : (
                                visitorDetailsList.map((vd, vIdx) => (
                                  <div key={vIdx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <div className="font-medium text-gray-900 mb-2">{vd.type || "Visitor"} ({(vd.details || []).length})</div>
                                    <ul className="space-y-2">
                                      {(vd.details || []).map((d, dIdx) => (
                                        <li key={dIdx} className="text-sm text-gray-700 flex flex-wrap gap-x-4 gap-y-1">
                                          <span><strong>Name:</strong> {d.name || "—"}</span>
                                          {d.phone ? <span><strong>Phone:</strong> {d.phone}</span> : null}
                                          {d.age != null ? <span><strong>Age:</strong> {d.age}</span> : null}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Pricing</div>
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
                              {pb?.breakdown && pb.breakdown.length > 0 ? (
                                <>
                                  {pb.breakdown.map((row, idx) => (
                                    <div key={idx} className="flex justify-between text-sm">
                                      <span className="text-gray-700">{row.type} × {row.count} @ {formatPriceSync(Number(row.pricePerPerson) || 0, currency)}</span>
                                      <span className="font-medium">{formatPriceSync(Number(row.subtotal) || 0, currency)}</span>
                                    </div>
                                  ))}
                                  <div className="border-t pt-2 mt-2 flex justify-between font-semibold text-gray-900">
                                    <span>Total</span>
                                    <span>{formatPriceSync(Number(pb.total ?? b.totalPrice) || 0, currency)}</span>
                                  </div>
                                </>
                              ) : (
                                <div className="flex justify-between font-semibold">
                                  <span>Total</span>
                                  <span>{formatPriceSync(Number(b.totalPrice) || 0, currency)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${b.paymentMethod === "wallet" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>
                              {b.paymentMethod === "wallet" ? "Wallet" : "Card"}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              b.status === "confirmed" || b.status === "completed" ? "bg-green-100 text-green-800" :
                              b.status === "cancelled" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                            }`}>
                              {b.status || "pending"}
                            </span>
                            {b.eTicketPdfPath && (
                              <button
                                type="button"
                                onClick={() => handleDownloadETicket(b._id, b.bookingReference)}
                                disabled={eTicketDownloadingId === b._id}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 text-sm font-medium hover:bg-blue-200 disabled:opacity-50"
                              >
                                {eTicketDownloadingId === b._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                {eTicketDownloadingId === b._id ? "Downloading…" : "Download e-ticket"}
                              </button>
                            )}
                          </div>
                          {b.adminDocuments && b.adminDocuments.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <UserDocumentViewer adminDocuments={b.adminDocuments} />
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Referral Link */}
          <section className="p-6 sm:p-8 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-slate-600" />
              Your Referral Link
            </h2>

          <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-100">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="w-full bg-transparent outline-none text-gray-700"
            />

            <button
              onClick={handleCopy}
              className="p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition"
            >
              {copied ? (
                <CheckCircle size={20} className="text-green-300" />
              ) : (
                <Copy size={20} />
              )}
            </button>

            <button
              onClick={handleShare}
              className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <Share2 size={20} />
            </button>
          </div>

            <p className="text-sm text-gray-500 mt-2">
              Share this link. When someone registers, your referral code will be auto-filled.
            </p>
          </section>
        </div>
      </div>

      {/* WALLET DETAIL MODAL */}
      {showWalletModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="relative px-6 py-5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm">
                    <Wallet className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">My Wallet Details</h2>
                    <p className="text-emerald-100 text-sm">
                      Status: {walletStatus.toUpperCase()} | Balance: {formatPriceSync(Number(userWallet) || 0, currency)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={fetchUserWalletAndRequests}
                    disabled={loading}
                    className="p-2 hover:bg-white/20 rounded-full transition-all duration-200 disabled:opacity-50"
                    title="Refresh"
                  >
                    <RefreshCw
                      className={`w-5 h-5 ${loading ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    onClick={() => setShowWalletModal(false)}
                    className="p-2 hover:bg-white/20 rounded-full transition-all duration-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveWalletTab("balance")}
                className={`flex-1 py-4 flex items-center justify-center gap-2 font-semibold transition-all ${
                  activeWalletTab === "balance"
                    ? "bg-white text-emerald-600 border-b-2 border-emerald-600 shadow-sm"
                    : "text-gray-600 hover:text-emerald-600 hover:bg-white/50"
                }`}
              >
                <Wallet className="w-4 h-4" />
                Balance
              </button>
              <button
                onClick={() => setActiveWalletTab("history")}
                className={`flex-1 py-4 flex items-center justify-center gap-2 font-semibold transition-all ${
                  activeWalletTab === "history"
                    ? "bg-white text-emerald-600 border-b-2 border-emerald-600 shadow-sm"
                    : "text-gray-600 hover:text-emerald-600 hover:bg-white/50"
                }`}
              >
                <History className="w-4 h-4" />
                Transaction History
              </button>
              <button
                onClick={() => setActiveWalletTab("requests")}
                className={`flex-1 py-4 flex items-center justify-center gap-2 font-semibold transition-all ${
                  activeWalletTab === "requests"
                    ? "bg-white text-emerald-600 border-b-2 border-emerald-600 shadow-sm"
                    : "text-gray-600 hover:text-emerald-600 hover:bg-white/50"
                }`}
              >
                <FileText className="w-4 h-4" />
                My Requests
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : (
                <>
                  {/* BALANCE TAB */}
                  {activeWalletTab === "balance" && (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-emerald-800">
                              Current Balance
                            </p>
                            <p className="text-4xl font-bold text-emerald-900 mt-1">
                              {formatPriceSync(Number(userWallet) || 0, currency)}
                            </p>
                            <p className="text-sm text-emerald-700 mt-2">
                              Status: {walletStatus.toUpperCase()}
                            </p>
                          </div>
                          <div className="p-4 bg-emerald-500/20 rounded-full">
                            <Wallet className="w-10 h-10 text-emerald-700" />
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-green-800">
                                Total Added
                              </p>
                              <p className="text-2xl font-bold text-green-900 mt-1">
                                {formatPriceSync(Number(stats.totalAdded) || 0, currency)}
                              </p>
                            </div>
                            <div className="p-3 bg-green-500/20 rounded-full">
                              <TrendingUp className="w-5 h-5 text-green-700" />
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-red-800">
                                Total Deducted
                              </p>
                              <p className="text-2xl font-bold text-red-900 mt-1">
                                {formatPriceSync(Number(stats.totalDeducted) || 0, currency)}
                              </p>
                            </div>
                            <div className="p-3 bg-red-500/20 rounded-full">
                              <TrendingDown className="w-5 h-5 text-red-700" />
                            </div>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-800">
                                Total Transactions
                              </p>
                              <p className="text-2xl font-bold text-blue-900 mt-1">
                                {stats.transactionCount}
                              </p>
                            </div>
                            <div className="p-3 bg-blue-500/20 rounded-full">
                              <History className="w-5 h-5 text-blue-700" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Actions based on status */}
                      {walletStatus === "active" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <button
                            onClick={() => setShowFundRequestModal(true)}
                            className="p-4 border-2 border-dashed border-purple-300 rounded-xl hover:border-purple-500 hover:bg-purple-50 transition-all flex flex-col items-center justify-center"
                          >
                            <Plus className="w-8 h-8 text-purple-600 mb-2" />
                            <span className="font-semibold text-purple-700">
                              Add Funds
                            </span>
                            <p className="text-sm text-gray-500 mt-1">
                              Request additional funds
                            </p>
                          </button>
                          <button
                            onClick={() => setActiveWalletTab("history")}
                            className="p-4 border-2 border-dashed border-blue-300 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all flex flex-col items-center justify-center"
                          >
                            <History className="w-8 h-8 text-blue-600 mb-2" />
                            <span className="font-semibold text-blue-700">
                              View History
                            </span>
                            <p className="text-sm text-gray-500 mt-1">
                              See all transactions
                            </p>
                          </button>
                        </div>
                      )}

                      {walletStatus === "not_active" && (
                        <div className="text-center p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                            Wallet Not Activated
                          </h3>
                          <p className="text-yellow-700 mb-4">
                            Activate your wallet to start using features
                          </p>
                          <button
                            onClick={handleWalletActivate}
                            className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 rounded-lg text-white hover:from-blue-700 hover:to-blue-800 transition-all"
                          >
                            Request Activation
                          </button>
                        </div>
                      )}

                      {walletStatus === "deactivated" && (
                        <div className="text-center p-6 bg-red-50 border border-red-200 rounded-xl">
                          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                          <h3 className="text-lg font-semibold text-red-800 mb-2">
                            Wallet Deactivated
                          </h3>
                          <p className="text-red-700 mb-4">
                            Your wallet has been deactivated. Request
                            reactivation to continue.
                          </p>
                          <button
                            onClick={handleWalletReactivate}
                            className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-3 rounded-lg text-white hover:from-orange-700 hover:to-orange-800 transition-all"
                          >
                            Request Reactivation
                          </button>
                        </div>
                      )}

                      {walletStatus === "pending" && (
                        <div className="text-center p-6 bg-yellow-50 border border-yellow-200 rounded-xl">
                          <Clock className="w-12 h-12 text-yellow-500 mx-auto mb-4 animate-pulse" />
                          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                            Request Pending
                          </h3>
                          <p className="text-yellow-700">
                            Your request is under review by admin team
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* HISTORY TAB - TAB 2 */}
                  {activeWalletTab === "history" && (
                    <>
                      {walletHistory.length === 0 ? (
                        <div className="text-center py-12">
                          <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-700">
                            No Transactions Yet
                          </h3>
                          <p className="text-gray-500 mt-1">
                            Your transaction history will appear here
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-emerald-800">
                                Total Transactions
                              </p>
                              <p className="text-2xl font-bold text-emerald-900">
                                {walletHistory.length}
                              </p>
                            </div>
                            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-green-800">
                                Total Credit
                              </p>
                              <p className="text-2xl font-bold text-green-900">
                                {formatPriceSync(
                                  walletHistory
                                    .filter((t) => t.type === "ADD")
                                    .reduce((sum, t) => sum + t.amount, 0),
                                  currency
                                )}
                              </p>
                            </div>
                            <div className="bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-red-800">
                                Total Debit
                              </p>
                              <p className="text-2xl font-bold text-red-900">
                                {formatPriceSync(
                                  walletHistory
                                    .filter((t) => t.type === "DEDUCT")
                                    .reduce((sum, t) => sum + t.amount, 0),
                                  currency
                                )}
                              </p>
                            </div>
                          </div>

                          <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                      Date & Time
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                      Type
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                      Amount
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                      Remarks
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {currentHistoryItems.map((w, i) => {
                                    const date = new Date(w.createdAt);
                                    const formattedDate =
                                      date.toLocaleDateString("en-IN", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      });
                                    const formattedTime =
                                      date.toLocaleTimeString("en-IN", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      });

                                    return (
                                      <tr
                                        key={i}
                                        className="hover:bg-gray-50 transition-colors"
                                      >
                                        <td className="px-6 py-4 text-gray-600">
                                          <div className="flex flex-col">
                                            <span className="font-medium">
                                              {formattedDate}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {formattedTime}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-6 py-4">
                                          <span
                                            className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${
                                              w.type === "ADD"
                                                ? "bg-emerald-100 text-emerald-800"
                                                : "bg-red-100 text-red-800"
                                            }`}
                                          >
                                            {w.type === "ADD" ? (
                                              <Plus className="w-3 h-3 mr-1" />
                                            ) : (
                                              <Minus className="w-3 h-3 mr-1" />
                                            )}
                                            {w.type}
                                          </span>
                                        </td>

                                        <td
                                          className={`px-6 py-4 font-semibold ${
                                            w.type === "ADD"
                                              ? "text-emerald-700"
                                              : "text-red-700"
                                          }`}
                                        >
                                          <div className="flex items-center">
                                            {w.type === "ADD" ? (
                                              <Plus className="w-4 h-4 mr-2 text-emerald-500" />
                                            ) : (
                                              <Minus className="w-4 h-4 mr-2 text-red-500" />
                                            )}
                                            {formatPriceSync(Number(w.amount) || 0, currency)}
                                          </div>
                                        </td>

                                        <td className="px-6 py-4 text-gray-700">
                                          <div
                                            className="max-w-xs truncate"
                                            title={w.remark || "Transaction"}
                                          >
                                            {w.remark || "Transaction"}
                                          </div>
                                        </td>

                                        <td className="px-6 py-4">
                                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <Check className="w-3 h-3 mr-1" />
                                            Completed
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Pagination */}
                            {walletHistory.length > 0 && (
                              <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
                                <div className="text-sm text-gray-700">
                                  Showing{" "}
                                  <span className="font-semibold">
                                    {indexOfFirstItem + 1}
                                  </span>{" "}
                                  to{" "}
                                  <span className="font-semibold">
                                    {Math.min(
                                      indexOfLastItem,
                                      walletHistory.length
                                    )}
                                  </span>{" "}
                                  of{" "}
                                  <span className="font-semibold">
                                    {walletHistory.length}
                                  </span>{" "}
                                  transactions
                                </div>

                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() =>
                                      handlePageChange(currentPage - 1)
                                    }
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <ChevronLeft className="w-5 h-5" />
                                  </button>

                                  {getPageNumbers().map((page, index) => (
                                    <React.Fragment key={index}>
                                      {page === "..." ? (
                                        <span className="px-3 py-1 text-gray-500">
                                          ...
                                        </span>
                                      ) : (
                                        <button
                                          onClick={() => handlePageChange(page)}
                                          className={`w-10 h-10 flex items-center justify-center rounded-lg font-medium transition-all ${
                                            currentPage === page
                                              ? "bg-emerald-600 text-white shadow-sm"
                                              : "border border-gray-300 hover:bg-gray-100 text-gray-700"
                                          }`}
                                        >
                                          {page}
                                        </button>
                                      )}
                                    </React.Fragment>
                                  ))}

                                  <button
                                    onClick={() =>
                                      handlePageChange(currentPage + 1)
                                    }
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <ChevronRight className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* REQUESTS TAB - TAB 3 */}
                  {activeWalletTab === "requests" && (
                    <>
                      {userRequests.length === 0 ? (
                        <div className="text-center py-12">
                          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-700">
                            No Requests Found
                          </h3>
                          <p className="text-gray-500 mt-1">
                            You haven't made any wallet requests
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-yellow-800">
                                Pending
                              </p>
                              <p className="text-2xl font-bold text-yellow-900">
                                {
                                  userRequests.filter(
                                    (r) => r.status === "pending"
                                  ).length
                                }
                              </p>
                            </div>
                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-green-800">
                                Approved
                              </p>
                              <p className="text-2xl font-bold text-green-900">
                                {
                                  userRequests.filter(
                                    (r) => r.status === "approved"
                                  ).length
                                }
                              </p>
                            </div>
                            <div className="bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl p-4">
                              <p className="text-sm font-medium text-red-800">
                                Rejected/Cancelled
                              </p>
                              <p className="text-2xl font-bold text-red-900">
                                {
                                  userRequests.filter(
                                    (r) =>
                                      r.status === "cancelled" ||
                                      r.status === "deactivated"
                                  ).length
                                }
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            {userRequests.map((req) => (
                              <div
                                key={req._id}
                                className={`p-5 border rounded-xl transition-all hover:shadow-md ${
                                  req.status === "pending"
                                    ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200"
                                    : req.status === "approved"
                                    ? "bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
                                    : "bg-gradient-to-r from-red-50 to-rose-50 border-red-200"
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div
                                        className={`p-2 rounded-lg ${
                                          req.requestType === "activation" ||
                                          req.requestType === "reactivation"
                                            ? "bg-blue-100 text-blue-700"
                                            : "bg-purple-100 text-purple-700"
                                        }`}
                                      >
                                        {req.requestType === "activation" ||
                                        req.requestType === "reactivation" ? (
                                          <User className="w-5 h-5" />
                                        ) : (
                                          <CreditCard className="w-5 h-5" />
                                        )}
                                      </div>
                                      <div>
                                        <h4 className="font-semibold text-gray-900">
                                          {req.requestType === "activation"
                                            ? "Wallet Activation Request"
                                            : req.requestType === "reactivation"
                                            ? "Wallet Reactivation Request"
                                            : "Fund Addition Request"}
                                          {req.requestType === "fund_add" &&
                                            req.requestedAmount > 0 && (
                                              <span className="ml-2 text-purple-700 font-bold">
                                                ({formatPriceSync(Number(req.requestedAmount) || 0, currency)})
                                              </span>
                                            )}
                                        </h4>
                                        <p className="text-sm text-gray-600 flex items-center gap-1">
                                          <Calendar className="w-4 h-4" />
                                          {formatDate(req.createdAt)}
                                        </p>
                                      </div>
                                    </div>

                                    {req.userNote && (
                                      <div className="mt-3 p-3 bg-white/50 rounded-lg">
                                        <p className="text-sm text-gray-700">
                                          <span className="font-medium">
                                            Your Note:
                                          </span>{" "}
                                          {req.userNote}
                                        </p>
                                      </div>
                                    )}

                                    {req.adminNote && (
                                      <div
                                        className={`mt-2 p-3 rounded-lg ${
                                          req.status === "approved"
                                            ? "bg-green-100 text-green-800"
                                            : req.status === "cancelled" ||
                                              req.status === "deactivated"
                                            ? "bg-red-100 text-red-800"
                                            : "bg-yellow-100 text-yellow-800"
                                        }`}
                                      >
                                        <p className="text-sm font-medium">
                                          {req.status === "approved"
                                            ? "Admin Response:"
                                            : req.status === "cancelled"
                                            ? "Cancellation Reason:"
                                            : "Admin Note:"}{" "}
                                          {req.adminNote}
                                        </p>
                                        {req.approvedAt && (
                                          <p className="text-xs mt-1">
                                            Approved on:{" "}
                                            {formatDate(req.approvedAt)}
                                          </p>
                                        )}
                                        {req.cancelledAt && (
                                          <p className="text-xs mt-1">
                                            Cancelled on:{" "}
                                            {formatDate(req.cancelledAt)}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="ml-4 text-right">
                                    <span
                                      className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${
                                        req.status === "pending"
                                          ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                                          : req.status === "approved"
                                          ? "bg-green-100 text-green-800 border border-green-200"
                                          : "bg-red-100 text-red-800 border border-red-200"
                                      }`}
                                    >
                                      {req.status === "pending" ? (
                                        <>
                                          <Clock className="w-3 h-3 mr-1 animate-pulse" />
                                          Pending
                                        </>
                                      ) : req.status === "approved" ? (
                                        <>
                                          <Check className="w-3 h-3 mr-1" />
                                          Approved
                                        </>
                                      ) : req.status === "deactivated" ? (
                                        <>
                                          <XCircle className="w-3 h-3 mr-1" />
                                          Deactivated
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="w-3 h-3 mr-1" />
                                          Cancelled
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Visa Applications */}
      <div className="max-w-4xl mx-auto mt-8 mb-12 px-4 sm:px-6">
        <div className="bg-white shadow-lg rounded-2xl border border-gray-200 overflow-hidden mt-8">
          <div className="p-6 sm:p-8">
            <UserVisaApplications />
          </div>
        </div>
      </div>

      {/* FUND REQUEST MODAL */}
      {showFundRequestModal && walletStatus === "active" && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Request Funds
              </h3>
              <button
                onClick={() => {
                  setShowFundRequestModal(false);
                  setFundAmount("");
                  setFundNote("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  value={fundAmount}
                  onChange={(e) => setFundAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  min="1"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (Optional)
                </label>
                <textarea
                  value={fundNote}
                  onChange={(e) => setFundNote(e.target.value)}
                  placeholder="Add note for admin..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowFundRequestModal(false);
                    setFundAmount("");
                    setFundNote("");
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFundRequest}
                  disabled={!fundAmount}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                    fundAmount
                      ? "bg-emerald-600 text-white hover:bg-emerald-700"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <CreditCard className="w-4 h-4" />
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REFERRAL MODAL */}
      {openModel && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
          <div className="bg-white w-[90%] sm:w-[500px] rounded-xl shadow-2xl p-6 animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                Referral Users
              </h3>
              <button
                onClick={() => setOpenModel(false)}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={22} />
              </button>
            </div>

            {refferanceData?.length === 0 ? (
              <p className="text-center text-gray-600 py-4">
                No referrals yet.
              </p>
            ) : (
              <div className="max-h-60 overflow-y-auto">
                <table className="w-full text-left border rounded-lg overflow-hidden">
                  <thead className="bg-gray-100 text-gray-700">
                    <tr>
                      <th className="p-2 border">#</th>
                      <th className="p-2 border">Name</th>
                      <th className="p-2 border">Email</th>
                      <th className="p-2 border">Phone</th>
                      <th className="p-2 border">Is Verified</th>
                    </tr>
                  </thead>

                  <tbody>
                    {refferanceData.map((data, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="p-2 border">{index + 1}</td>
                        <td className="p-2 border">{data?.username}</td>
                        <td className="p-2 border">{data?.email}</td>
                        <td className="p-2 border">{data?.phone}</td>
                        <td className="p-2 border">
                          {data?.isVerified ? (
                            <span className="text-green-600 font-medium">
                              Yes
                            </span>
                          ) : (
                            <span className="text-red-600 font-medium">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="text-right mt-4">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                onClick={() => setOpenModel(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-center"
        autoClose={3000}
        theme="colored"
        toastClassName="rounded-xl"
      />
    </>
  );
};

export default UserProfile;
